import React, { useEffect, useRef } from 'react';

interface GoldSparkle {
  x: number;
  y: number;
  size: number;
  velocity: { x: number; y: number };
  opacity: number;
  phase: number;
  type: 'sparkle' | 'star';
  rotation: number;
}

export const GoldSparkles: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sparklesRef = useRef<GoldSparkle[]>([]);
  const animationFrameRef = useRef<number>();
  const lastUpdateRef = useRef<number>(0);

  const drawStar = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number, rotation: number) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotation);
    ctx.beginPath();
    for (let i = 0; i < 5; i++) {
      const angle = (i * 4 * Math.PI) / 5;
      const x1 = Math.cos(angle) * size;
      const y1 = Math.sin(angle) * size;
      if (i === 0) {
        ctx.moveTo(x1, y1);
      } else {
        ctx.lineTo(x1, y1);
      }
    }
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    ctx.imageSmoothingEnabled = false;

    const resizeCanvas = () => {
      const scale = 0.5;
      canvas.width = window.innerWidth * scale;
      canvas.height = window.innerHeight * scale;
      canvas.style.width = '100%';
      canvas.style.height = '100%';
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const createSparkle = (): GoldSparkle => {
      const type = Math.random() > 0.7 ? 'star' : 'sparkle';
      return {
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: type === 'star' ? Math.random() * 3 + 2 : Math.random() * 2 + 1,
        velocity: {
          x: (Math.random() - 0.5) * 0.3,
          y: (Math.random() - 0.5) * 0.3
        },
        opacity: Math.random(),
        phase: Math.random() * Math.PI * 2,
        type,
        rotation: Math.random() * Math.PI * 2
      };
    };

    // Réduction de 50 à 30 particules (40% de réduction)
    const numSparkles = 30;
    for (let i = 0; i < numSparkles; i++) {
      sparklesRef.current.push(createSparkle());
    }

    const animate = (timestamp: number) => {
      if (!ctx || !canvas) return;

      if (timestamp - lastUpdateRef.current < 33) {
        animationFrameRef.current = requestAnimationFrame(animate);
        return;
      }
      lastUpdateRef.current = timestamp;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      sparklesRef.current.forEach((sparkle, index) => {
        sparkle.phase += 0.02;
        sparkle.opacity = (Math.sin(sparkle.phase) * 0.3 + 0.5) * 0.7;
        sparkle.rotation += 0.01;

        sparkle.x += sparkle.velocity.x;
        sparkle.y += sparkle.velocity.y;

        ctx.fillStyle = sparkle.type === 'star' 
          ? `rgba(255, 223, 0, ${sparkle.opacity})` 
          : `rgba(255, 215, 0, ${sparkle.opacity})`;

        if (sparkle.type === 'star') {
          drawStar(ctx, sparkle.x, sparkle.y, sparkle.size, sparkle.rotation);
        } else {
          ctx.beginPath();
          ctx.arc(sparkle.x, sparkle.y, sparkle.size, 0, Math.PI * 2);
          ctx.fill();
        }

        if (sparkle.x < 0 || sparkle.x > canvas.width || 
            sparkle.y < 0 || sparkle.y > canvas.height) {
          sparklesRef.current[index] = createSparkle();
        }
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate(0);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-10"
      style={{ 
        mixBlendMode: 'screen',
        opacity: 0.8,
        willChange: 'transform'
      }}
    />
  );
};
